/**
 * "Rota do período" da Visão Executiva: o faturamento acumulado dia a dia,
 * montado a partir de `overview.cteByDay` — a mesma série do gráfico
 * "Faturamento CT-e por Dia". Não consulta nada novo.
 *
 * Com o período "mês atual" (que vai só até hoje), a rota cobre o mês inteiro e
 * traz a projeção de fechamento NO RITMO ATUAL: média diária até hoje × dias do
 * mês. É uma estimativa aritmética, rotulada como tal na tela — não é meta nem previsão.
 */

const DAY = 86_400_000;

/** Meia-noite de São Paulo (UTC-3, sem horário de verão) da data civil y/m/d. */
const spMidnight = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d, 3, 0, 0));

/** Data civil de SP de um instante. */
function spDate(t: Date) {
  const sp = new Date(t.getTime() - 3 * 3600_000);
  return { y: sp.getUTCFullYear(), m: sp.getUTCMonth(), d: sp.getUTCDate() };
}

const isoDay = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export interface RoutePoint {
  /** Dia do período, começando em 1. */
  day: number;
  /** Faturamento acumulado até o fim do dia. */
  cumulative: number;
}

export interface MonthRoute {
  points: RoutePoint[];
  daysTotal: number;
  /** Dias já percorridos (inclui hoje). */
  daysElapsed: number;
  /** Faturamento acumulado até hoje. */
  total: number;
  /** Fechamento estimado no ritmo atual; null quando o período já terminou. */
  projected: number | null;
  /** Primeiro e último dia da rota (ISO, para rótulos). */
  start: Date;
  end: Date;
  /** Rota de um período ainda em andamento. */
  inProgress: boolean;
}

export function buildMonthRoute(opts: {
  cteByDay: Array<{ date: string; totalValue: number }>;
  period: string;
  /** ISO do filtro (fim exclusivo). */
  from: string;
  to: string;
  now?: Date;
}): MonthRoute | null {
  const now = opts.now ?? new Date();
  const start = new Date(opts.from);
  const filterEnd = new Date(opts.to);
  if (!(filterEnd > start)) return null;

  // "Mês atual" vai só até hoje: a rota mostra o mês inteiro para dar o horizonte.
  let end = filterEnd;
  if (opts.period === "current_month") {
    const s = spDate(start);
    end = spMidnight(s.y, s.m + 1, 1);
  }

  const daysTotal = Math.round((end.getTime() - start.getTime()) / DAY);
  const reached = Math.min(now.getTime(), filterEnd.getTime());
  const daysElapsed = Math.max(0, Math.min(daysTotal, Math.ceil((reached - start.getTime()) / DAY)));
  if (daysTotal <= 0) return null;

  const byDay = new Map<string, number>();
  for (const r of opts.cteByDay) byDay.set(r.date.slice(0, 10), (byDay.get(r.date.slice(0, 10)) ?? 0) + r.totalValue);

  const s = spDate(start);
  const points: RoutePoint[] = [];
  let acc = 0;
  for (let i = 0; i < daysElapsed; i++) {
    const d = new Date(Date.UTC(s.y, s.m, s.d + i));
    acc += byDay.get(isoDay(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())) ?? 0;
    points.push({ day: i + 1, cumulative: acc });
  }

  const inProgress = end.getTime() > reached && daysElapsed > 0;
  // Só projeta quando ainda há dias pela frente (com "hoje", o período é o próprio dia).
  const projected = inProgress && daysElapsed < daysTotal ? (acc / daysElapsed) * daysTotal : null;

  return {
    points, daysTotal, daysElapsed, total: acc, projected,
    start, end: new Date(end.getTime() - 1), inProgress,
  };
}
